/**
 * Append an entry to the admin activity log.
 *
 * WHY THIS EXISTS
 *   `public.admin_activity_log` was fully built and never written to. The table
 *   exists, RLS is correct (any admin may INSERT, only super_admin may SELECT,
 *   DELETE is `false` so it is append-only), `useAdminActivityLog` reads it and
 *   resolves admin emails, and the dashboard renders it with per-action icons,
 *   colours and human labels. Every part was finished except the writer, so the
 *   "Admin Activity" panel was permanently empty by construction — a reviewer
 *   would see an empty feed and conclude nothing had happened, rather than that
 *   nothing was being recorded. Produced but not wired.
 *
 * THE VOCABULARY IS NOT FREE-FORM
 *   `AdminAction` mirrors the keys `src/components/admin/AdminActivityLog.tsx`
 *   already switches on. A value outside it still renders — the component
 *   humanises unknown actions — but it renders without an icon or colour, so
 *   new actions belong in both places.
 *
 * FAILURE POLICY: FAIL SOFT, NEVER SILENT
 *   A failed audit write must not take down the admin action it describes —
 *   losing a log line is worse than losing the change, but not by enough to
 *   justify blocking support work behind the audit table. So this never throws.
 *   It DOES report, because a silently-dropped audit write is how a log ends up
 *   empty and trusted at the same time — exactly the state this file fixes.
 *
 *   Note the AAL2 gate applies to this table (`mfa_aal2_gate`), so an admin on
 *   an AAL1 session cannot write here. That surfaces as a reported warning
 *   rather than a broken screen.
 */

import { supabase } from '@/lib/supabase';
import { reportError } from '@/lib/errors';
import { mode } from '@/lib/config/app-mode';

/** Actions the activity feed knows how to render. Keep in sync with AdminActivityLog.tsx. */
export type AdminAction =
  | 'feedback_status_changed'
  | 'feedback_resolved'
  | 'feedback_priority_changed'
  | 'note_added'
  | 'role_changed'
  | 'feature_flag_updated'
  | 'retention_config_updated'
  | 'retention_run_started';

export interface AdminActionEntry {
  action: AdminAction;
  /** What kind of thing was acted on, e.g. 'feedback', 'feature_flag'. */
  targetType?: string;
  /** Identifier of the thing acted on. Text, because targets are not all uuids. */
  targetId?: string;
  /**
   * Small, structured context the feed renders — e.g. `{ from, to }` for a
   * status change. Keep it to what a human reading the feed needs; this table
   * is readable by super_admins and is not a place for personal data.
   */
  details?: Record<string, unknown>;
}

export async function logAdminAction(entry: AdminActionEntry): Promise<void> {
  // Mock/demo mode has no server and its own fixture feed.
  if (mode.showSampleData) return;

  try {
    const { data: auth } = await supabase.auth.getUser();
    const adminUserId = auth.user?.id;
    if (!adminUserId) return;

    const { error } = await supabase.from('admin_activity_log').insert({
      admin_user_id: adminUserId,
      action: entry.action,
      target_type: entry.targetType ?? null,
      target_id: entry.targetId ?? null,
      details: entry.details ?? {},
    } as never);

    // supabase-js resolves with { error } rather than throwing (ARCH-06), so
    // this check is the only thing standing between a failed audit write and
    // silence.
    if (error) {
      reportError(error, {
        context: `logAdminAction.${entry.action}`,
        level: 'warning',
        silent: true,
      });
    }
  } catch (err) {
    reportError(err, {
      context: `logAdminAction.${entry.action}`,
      level: 'warning',
      silent: true,
    });
  }
}
